import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense } from 'preact/compat';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { useRouter } from 'preact-router';
import { useDispatch } from 'react-redux';

import { useFetchUserIfNeeded } from '@/components/Authorize/hooks';
import { useWindowMediaQuery } from '@/hooks/useWindowMediaQuery';
import { AnyCard } from '@/queries/cards/types';
import { getHabitatQueryOptions } from '@/queries/habitats';
import { setHabitat, unsetHabitat } from '@/routes/habitat/actions';
import AnimalGrid from '@/routes/habitat/components/animals/AnimalGrid';

import EmbeddedHabitatMediaGrid from './MediaGrid';
import EmbeddedHabitatSearchBar from './SearchBar';

const LazyCardsModal = lazy(() => import('@/routes/habitat/components/CardTabs/Mobile/CardsModal'));

const EmbeddedHabitat = () => {
  const dispatch = useDispatch();
  const [{ matches }] = useRouter();
  const { data: habitat } = useQuery(getHabitatQueryOptions(matches?.habitatSlug));
  const isDesktopSize = useWindowMediaQuery('(min-width: 768px)');

  const [selectedAnimalCards, setSelectedAnimalCards] = useState<AnyCard[] | null>(null);
  const [cardIndex, setCardIndex] = useState(0);

  const onNextCard = useCallback(() => setCardIndex((prev) => prev + 1), []);
  const onPrevCard = useCallback(() => setCardIndex((prev) => Math.max(prev - 1, 0)), []);
  const onClose = useCallback(() => {
    setSelectedAnimalCards(null);
  }, []);
  const onSelectAnimal = useCallback((animalId: string, cards: AnyCard[]) => {
    setSelectedAnimalCards(cards);
    setCardIndex(0);
  }, []);

  useFetchUserIfNeeded();
  useEffect(() => {
    if (!habitat) {
      return undefined;
    }
    dispatch(setHabitat({ habitat }));
    return () => {
      dispatch(unsetHabitat());
    };
  }, [habitat, dispatch]);

  return (
    <div className="relative flex flex-col md:flex-row">
      <div className="relative bg-primary-green text-white max-md:aspect-video md:h-[588px] md:grow">
        <video
          src={habitat?.heroVideoURL || habitat?.trailer?.videoURL}
          autoPlay
          muted
          loop
          className="preflight preflight-video size-full object-cover"
        />
        {/* Search bar */}
        <div className="absolute inset-0 items-center justify-center p-4 md:flex">
          <EmbeddedHabitatSearchBar placeholder={`Watch ${habitat?.animal ?? 'animals'} and more...`} />
        </div>
      </div>

      <div className="relative shrink-0 bg-white md:h-[588px] md:w-[380px]">
        {habitat && (
          <>
            <div className="customScrollBar grey !overflow-x-auto !overflow-y-hidden">
              <AnimalGrid
                habitatId={habitat._id}
                animals={habitat.animals}
                className="flex w-max gap-x-4 px-5 pt-4 md:pb-3 md:pt-5"
                thumbnailClassName="size-[72px]"
                titleClassName="text-sm/none font-semibold"
                onSelectAnimal={onSelectAnimal}
              />
            </div>

            <div className="px-5 pt-3">
              <section>
                <h4 className="preflight preflight-h4 text-xs font-normal text-[#57534E]">Moments Ago</h4>
                <EmbeddedHabitatMediaGrid
                  className="my-2 grid grid-cols-2 gap-2"
                  mediaQueryParams={{ habitatId: habitat._id, pageSize: 2, mediaType: 'video', videoType: 'clip' }}
                  pageSize={2}
                />
              </section>

              <section>
                <h4 className="preflight preflight-h4 text-xs font-normal text-[#57534E]">Top Moments this Week</h4>
                <EmbeddedHabitatMediaGrid
                  className="my-2 grid grid-cols-2 gap-2"
                  mediaQueryParams={{
                    habitatId: habitat._id,
                    pageSize: 6,
                    mediaType: 'video',
                    videoType: 'clip',
                    highlight: (0b11).toString(), // TODO: confirm the highlight value
                  }}
                  pageSize={6}
                />
              </section>
            </div>

            {selectedAnimalCards && (
              <Suspense fallback={null}>
                <LazyCardsModal
                  relative={isDesktopSize}
                  loading={false}
                  cards={selectedAnimalCards}
                  activeCardIndex={cardIndex}
                  onNextCard={onNextCard}
                  onPrevCard={onPrevCard}
                  onClose={onClose}
                />
              </Suspense>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmbeddedHabitat;
